import { FormPageContainer } from '@strata/react-form-header-and-content';
import {
    type ColumnDef,
    type FilterCategory,
    GridWithFilters,
    type KpiData,
    ProductCell,
} from '@strata/react-grid';
import { useNavigate } from 'react-router';

import { ProductStatsColumns } from 'components/products/ProductStatsColumns';
import { TimeFrameBanner } from 'components/shared/TimeframeBanner';
import { ViewsControls } from 'components/shared/ViewsControls';
import { AppRoutes } from 'services/routes';
import { useAppSelector } from 'store';
import { type MedicationSummary, useGetMedicationsQuery } from 'store/api/apertureApi';
import { useGetPayerDetailsQuery } from 'store/api/referenceApi';
import { Grids } from 'store/grids/gridsSlice';
import { useGridState } from 'store/grids/gridsThunks';

const initialSortProperties = [{ id: 'drugName', desc: false }];

const filterCategories: FilterCategory<MedicationSummary>[] = [
    { key: 'therapeuticCategory', title: 'Therapeutic Category' },
    { key: 'drugName', title: 'Product' },
    {
        key: 'isBrand',
        title: 'Brand/Generic',
        valueDisplayTextMap: { true: 'Brand', false: 'Generic' },
    },
];

const kpiData: KpiData<MedicationSummary>[] = [
    {
        label: 'Active Referrals',
        value: (data) =>
            data?.reduce((total, product) => total + (product.numberOfReferrals ?? 0), 0),
    },
];

const columns: ColumnDef<MedicationSummary>[] = [
    {
        accessorKey: 'drugName',
        header: 'Product',
        size: 150,
        cell: ({ row, getValue }) => (
            <ProductCell product={getValue()} isBrand={row.original.isBrand} primary />
        ),
    },
    ...(ProductStatsColumns as ColumnDef<MedicationSummary>[]),
];

export const ProductsPage = () => {
    const navigate = useNavigate();
    const { payerId } = useAppSelector((state) => state.auth);

    const { data: payerDetails } = useGetPayerDetailsQuery({ payerId: payerId! });

    const { segmentId } = useAppSelector((state) => state.views);
    const { data, isFetching } = useGetMedicationsQuery({ segmentId });

    const {
        sort,
        search,
        activeFilters,
        filterPaneWidth,
        updateGridSort,
        updateGridSearch,
        updateGridActiveFilters,
        updateFilterPaneWidth,
    } = useGridState(Grids.PRODUCTS, initialSortProperties);

    return (
        <FormPageContainer>
            <GridWithFilters
                title="Products"
                secondaryTitle={payerDetails?.payerName}
                headerContent={<TimeFrameBanner />}
                viewsPaneContent={<ViewsControls />}
                data={data}
                columns={columns}
                isLoading={isFetching}
                sortProperties={sort}
                onSort={updateGridSort}
                enableSearch
                searchText={search}
                onSearch={updateGridSearch}
                filterCategories={filterCategories}
                activeFilters={activeFilters}
                onFilter={updateGridActiveFilters}
                filterPaneRenderWidth={filterPaneWidth}
                onFilterPaneResize={updateFilterPaneWidth}
                kpiData={kpiData}
                onRowClick={(rowData) =>
                    navigate(
                        AppRoutes.productDetails(
                            rowData.productServiceOid,
                            rowData.isBrand,
                            rowData.referentPriceType,
                        ),
                    )
                }
            />
        </FormPageContainer>
    );
};
